import React from 'react';
import { FileText, Award, CheckCircle, Leaf } from 'lucide-react';

const CutleryCertifications = () => {
  const certifications = [
    {
      name: "FSSC 22000",
      description: "Food Safety System Certification 22000 - Ensuring the highest standards of food safety management for all cutlery products",
      category: "Food Safety",
      icon: <Award size={32} />,
      pdfUrl: "/pdfs/FSSC_22000_Cutlery.pdf",
      fileName: "FSSC_22000_Cutlery.pdf"
    },
    {
      name: "Vegan Certification",
      description: "Certified 100% vegan cutlery - Made entirely from plant-based ingredients",
      category: "Dietary",
      icon: <CheckCircle size={32} />,
      pdfUrl: "/pdfs/Vegan_Certificate_Cutlery.pdf",
      fileName: "Vegan_Certificate_Cutlery.pdf"
    },
    {
      name: "Halal Certification",
      description: "Halal certified cutlery meeting Islamic dietary requirements",
      category: "Dietary",
      icon: <CheckCircle size={32} />,
      pdfUrl: "/pdfs/Halal_Certificate_Cutlery.pdf",
      fileName: "Halal_Certificate_Cutlery.pdf"
    },
    {
      name: "EU Food Contact Materials",
      description: "Compliant with EU regulations for food contact materials (EC 1935/2004)",
      category: "Regulatory",
      icon: <Award size={32} />,
      pdfUrl: "/pdfs/EU_FCM_Cutlery.pdf",
      fileName: "EU_FCM_Cutlery.pdf"
    },
    {
      name: "Biodegradable Certification",
      description: "Certified 100% biodegradable - Breaks down naturally within 90 days",
      category: "Environmental",
      icon: <Leaf size={32} />,
      pdfUrl: "/pdfs/Biodegradable_Cutlery.pdf",
      fileName: "Biodegradable_Cutlery.pdf"
    }
  ];

  const specifications = [
    {
      name: "Edible Spoons",
      description: "Complete product specification for our edible spoons - Perfect for soups, desserts, and more",
      type: "Spoon",
      icon: <FileText size={32} />,
      pdfUrl: "/pdfs/Edible_Spoons_Specification.pdf",
      fileName: "Edible_Spoons_Specification.pdf"
    },
    {
      name: "Edible Forks",
      description: "Complete product specification for our edible forks - Ideal for salads and main courses",
      type: "Fork",
      icon: <FileText size={32} />,
      pdfUrl: "/pdfs/Edible_Forks_Specification.pdf",
      fileName: "Edible_Forks_Specification.pdf"
    },
    {
      name: "Edible Coffee Stirrers",
      description: "Complete product specification for our edible coffee stirrers - Sweet complement to your beverage",
      type: "Stirrer",
      icon: <FileText size={32} />,
      pdfUrl: "/pdfs/Coffee_Stirrer_Specification.pdf",
      fileName: "Coffee_Stirrer_Specification.pdf"
    },
    {
      name: "Edible Ice Cream Spoons",
      description: "Complete product specification for our edible ice cream spoons - The perfect pairing",
      type: "Spoon",
      icon: <FileText size={32} />,
      pdfUrl: "/pdfs/Ice_Cream_Spoon_Specification.pdf",
      fileName: "Ice_Cream_Spoon_Specification.pdf"
    },
    {
      name: "French Fries Fork",
      description: "Complete product specification for our French fries fork - Fun and functional",
      type: "Fork",
      icon: <FileText size={32} />,
      pdfUrl: "/pdfs/French_Fries_Fork_Specification.pdf",
      fileName: "French_Fries_Fork_Specification.pdf"
    }
  ];

  return (
    <div className="certifications-page">
      {/* Hero Section */}
      <section className="certifications-hero">
        <div className="container">
          <h1 className="display-medium">Cutlery & Tableware Certifications</h1>
          <p className="body-large">
            Discover our comprehensive certifications and detailed product specifications for edible cutlery. 
            All documentation available for download to ensure transparency and compliance.
          </p>
        </div>
      </section>

      {/* Certifications Section */}
      <section className="certifications-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Cutlery Certifications</h2>
            <p className="body-large">
              Our edible cutlery products meet the highest industry standards with internationally recognized certifications.
            </p>
          </div>

          <div className="certifications-grid">
            {certifications.map((cert, index) => (
              <div key={index} className="certification-card hover-lift">
                <div className="certification-header">
                  <div className="certification-icon" style={{ color: 'var(--brand-dark)' }}>
                    {cert.icon}
                  </div>
                </div>
                
                <h3 className="certification-name">{cert.name}</h3>
                <p className="certification-description">{cert.description}</p>
                
                <div className="certification-actions">
                  <button
                    onClick={() => window.open(cert.pdfUrl, '_blank')}
                    className="btn-primary view-btn"
                  >
                    <FileText size={18} /> View Certificate
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Specifications Section */}
      <section className="specifications-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Cutlery Product Specifications</h2>
            <p className="body-large">
              Detailed technical specifications for our edible cutlery products.
            </p>
          </div>

          <div className="specifications-grid">
            {specifications.map((spec, index) => (
              <div key={index} className="specification-card hover-lift">
                <div className="specification-header">
                  <div className="specification-icon" style={{ color: 'var(--brand-dark)' }}>
                    {spec.icon}
                  </div>
                </div>
                
                <h3 className="specification-name">{spec.name}</h3>
                <p className="specification-description">{spec.description}</p>
                
                <div className="specification-actions">
                  <button
                    onClick={() => window.open(spec.pdfUrl, '_blank')}
                    className="btn-primary view-btn"
                  >
                    <FileText size={18} /> View Specifications
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="certifications-cta">
        <div className="container">
          <div className="cta-content">
            <h2 className="heading-1">Need More Information?</h2>
            <p className="body-large">
              Have questions about our cutlery certifications or need additional documentation? 
              Our team is here to help with any technical or compliance inquiries.
            </p>
            <div className="cta-actions">
              <a
                href="mailto:info@magomeal.nl?subject=Cutlery%20Certification%20Inquiry"
                className="btn-primary"
                style={{ display: 'inline-block' }}
              >
                Contact Our Team
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CutleryCertifications;
